<template>
  <div class="profile">
    <div class="row">
      <!-- Left Column - Profile Sidebar -->
      <div class="col-lg-5 col-xxl-3">
        <profile-sidebar 
          :current-user="currentUser" 
          :profile="profile"
          :is-loading="isLoading"
          ref="profileSidebar"
        />
      </div>
      
      <!-- Right Column - Content Area -->
      <div class="col-lg-7 col-xxl-9">
        <div class="tab-content" id="user-set-tabContent">
          <!-- Profile Overview Tab -->
          <div class="tab-pane fade show active" id="user-set-profile" role="tabpanel"
            aria-labelledby="user-set-profile-tab">
            
            <profile-about-card 
              :about="profile.about" 
              :is-loading="isLoading"
              @updated="loadCompanyProfile" 
            />
            
            <!-- Tabs for Products and Catalogue - Show only if user has permissions geçiçi olarak kapatıldı. -->
            <div v-if="showProductsAndServicesTab" class="card">
              <div class="card-body py-0">
                <ul class="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                  <li v-if="canViewMaterialCategories()" class="nav-item">
                    <a class="nav-link active" id="profile-tab" data-bs-toggle="tab" href="#profile" role="tab"
                      aria-selected="true">
                      <i class="ti ti-user-circle me-2"></i> {{ $t('profile.sections.products') }}
                    </a>
                  </li>
                  <li v-if="canViewMaterials()" class="nav-item">
                    <a class="nav-link" :class="{ active: !canViewMaterialCategories() }" id="followers-tab" data-bs-toggle="tab" href="#followers" role="tab"
                      aria-selected="false">
                      <i class="ti ti-users me-2"></i> {{ $t('profile.sections.catalogue') }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            
            <!-- Tab Contents -->
            <div v-if="showProductsAndServicesTab" class="tab-content" id="myTabContent">
              <!-- Products Tab -->
              <div v-if="canViewMaterialCategories()" class="tab-pane fade show active" id="profile" role="tabpanel" aria-labelledby="profile-tab">
                <profile-categories-card 
                  :tenant-id="currentUser?.tenantId" 
                  :can-create-material-categories="canCreateMaterialCategories()"
                  :can-edit-material-categories="canEditMaterialCategories()"
                  :can-delete-material-categories="canDeleteMaterialCategories()"
                  @categories-updated="handleCategoriesUpdated"
                />
              </div>
              
              <!-- Catalogue Tab -->
              <div v-if="canViewMaterials()" class="tab-pane fade" :class="{ 'show active': !canViewMaterialCategories() }" id="followers" role="tabpanel" aria-labelledby="followers-tab">
                <profile-materials-card 
                  :tenant-id="currentUser?.tenantId" 
                  :can-create-materials="canCreateMaterials()"
                  :can-edit-materials="canEditMaterials()"
                  :can-delete-materials="canDeleteMaterials()"
                />
              </div>
            </div>
            
            <!-- Certifications - Always show outside of tabs -->
            <profile-certifications-card @certificates-updated="handleCertificatesUpdated" />
          </div>
          
          <!-- Account Information Tab -->
          <div class="tab-pane fade" id="user-set-account" role="tabpanel" aria-labelledby="user-set-account-tab">
            <profile-account-form 
              :profile="profile" 
              :is-saving="isSaving" 
              :is-loading="isLoading"
              @submit="updateProfile" 
            />
          </div>
          
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import tenantInfoService from '@/services/tenantInfoService';
import { useMaterialCategoriesPermissions } from '@/composables/useMaterialCategoriesPermissions';
import { useMaterialPermissions } from '@/composables/useMaterialPermissions';
import { usePortPermissions } from '@/composables/usePortPermissions';

// Import component files
import ProfileSidebar from '@/components/profile/ProfileSidebar.vue';
import ProfileAboutCard from '@/components/profile/ProfileAboutCard.vue';
import ProfileCategoriesCard from '@/components/profile/ProfileCategoriesCard.vue';
import ProfileCertificationsCard from '@/components/profile/ProfileCertificationsCard.vue';
import ProfileMaterialsCard from '@/components/profile/ProfileMaterialsCard.vue';
import ProfileAccountForm from '@/components/profile/ProfileAccountForm.vue';
 

export default {
  name: 'Profile',
  components: {
    ProfileSidebar,
    ProfileAboutCard,
    ProfileCategoriesCard,
    ProfileCertificationsCard,
    ProfileMaterialsCard,
    ProfileAccountForm
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const profileSidebar = ref(null);
    
    // Material Categories permissions
    const {
      canViewMaterialCategories,
      canCreateMaterialCategories,
      canEditMaterialCategories,
      canDeleteMaterialCategories
    } = useMaterialCategoriesPermissions();
    
    // Materials permissions
    const {
      canViewMaterials,
      canCreateMaterials,
      canEditMaterials,
      canDeleteMaterials
    } = useMaterialPermissions();
    
    // Port permissions
    const {
      canAssignPortToTenant,
      canEditPorts,
      canDeletePorts
    } = usePortPermissions();
    
    const currentUser = computed(() => store.getters['auth/currentUser']);
    
    // Check if Products & Services tab should be shown
    const showProductsAndServicesTab = computed(() => {
      // geçiçi olarak kapatıldı.
      return false
      return canViewMaterialCategories() || canViewMaterials();
    });
    
    // Sample dummy data for products
    const products = ref([
      { 
        name: '6XV1870-8AH10 Siemens', 
        category: 'Category: Ethernet', 
        code: '7199 - PCE',
        image: '/template/src/assets/images/application/img-prod-1.jpg'
      },
      { 
        name: '6SL3203-0CE13-2AA0 Siemens', 
        category: 'Category: Sinamics Converters', 
        code: '6058 - PCE',
        image: '/template/src/assets/images/application/img-prod-2.jpg'
      },
      { 
        name: '6SL3210-1PB13-8ALO Siemens', 
        category: 'Category: Sinamics Converters', 
        code: '6322 - PCE',
        image: '/template/src/assets/images/application/img-prod-3.jpg'
      },
      { 
        name: '6GK1704-5CW00-3AE1 Siemens', 
        category: 'Category: Simatic NET', 
        code: '7457 - PCE',
        image: '/template/src/assets/images/application/img-prod-4.jpg'
      },
      { 
        name: '6SL3210-1PB21-0UL0 Siemens', 
        category: 'Category: Sinamics Converters', 
        code: '6239 - PCE',
        image: '/template/src/assets/images/application/img-prod-5.jpg'
      },
      { 
        name: 'HMIVMMAC1 Schneider Electric', 
        category: 'Category: Schneider Electric', 
        code: 'Power Supply Module',
        image: '/template/src/assets/images/application/img-prod-6.jpg'
      }
    ]);
    
    // Company profile data (tenant extra properties)
    const profile = ref({
      displayName: '',
      email: '',
      phone: '',
      about: '',
      companyLogoId: ''
    });
    
    const isSaving = ref(false);
    
    const isLoading = ref(true);
    
    // Load tenant extra properties for company profile
    const loadCompanyProfile = async () => {
      console.log('🔄 Profile.vue: Starting loadCompanyProfile');
      try {
        const extraProperties = await tenantInfoService.getExtraProperties();
        console.log('📊 Profile.vue: Extra properties loaded:', extraProperties);
        
        // Update profile data
        Object.assign(profile.value, {
          displayName: extraProperties.displayName || '',
          email: extraProperties.email || '',
          phone: extraProperties.phone || '',
          about: extraProperties.about || '',
          companyLogoId: extraProperties.companyLogoId || ''
        });
        
        console.log('✅ Profile.vue: Profile object updated:', profile.value);
      } catch (error) {
        console.error('❌ Profile.vue: Error loading company profile:', error);
        store.dispatch('notify', {
          message: 'Failed to load company profile',
          type: 'error'
        });
      } finally {
        isLoading.value = false;
        console.log('🏁 Profile.vue: loadCompanyProfile completed, isLoading:', isLoading.value);
      }
    };
    
    // Load initial data before mounting
    if (currentUser.value) {
      loadCompanyProfile();
    }
    
    // Setup component after initial load
    onMounted(async () => {
      if (!currentUser.value) {
        // Redirect to login if not authenticated
        router.push('/login');
        return;
      }
      
      // Initialize Bootstrap tabs and popovers
      setTimeout(() => {
        if (window.bootstrap) {
          const tabEl = document.querySelectorAll('a[data-bs-toggle="tab"], a[data-bs-toggle="pill"]');
          tabEl.forEach(el => {
            new window.bootstrap.Tab(el);
          });
        }
      }, 100);
    });
    
    const updateProfile = async (formData) => {
      isSaving.value = true;
      
      try {
        // Save company profile via tenant extra properties API
        const updatedProperties = await tenantInfoService.saveExtraProperties(formData);
        
        // Update local profile data
        profile.value = {
          displayName: updatedProperties.displayName || '',
          email: updatedProperties.email || '',
          phone: updatedProperties.phone || '',
          about: updatedProperties.about || '',
          companyLogoId: updatedProperties.companyLogoId || ''
        };
        
        store.dispatch('notify', {
          message: 'Company profile updated successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('Error updating company profile:', error);
        store.dispatch('notify', {
          message: 'Failed to update company profile',
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };
    
    
    
    // Handle certificate updates from certificates card
    const handleCertificatesUpdated = async (data) => {

      // Force reload entity counts in sidebar if needed
      if (profileSidebar.value && profileSidebar.value.loadEntityCounts) {
        await profileSidebar.value.loadEntityCounts();
      }
    };
    
    // Handle category updates from categories card
    const handleCategoriesUpdated = async (data) => {
      // Force reload entity counts in sidebar if needed
      if (profileSidebar.value && profileSidebar.value.loadEntityCounts) {
        await profileSidebar.value.loadEntityCounts();
      }
    };
    
    return {
      currentUser,
      profile,
      products,
      isSaving,
      isLoading,
      updateProfile,
      
      profileSidebar,
      handleCertificatesUpdated,
      handleCategoriesUpdated,
      loadCompanyProfile,
      showProductsAndServicesTab,
      canViewMaterialCategories,
      canViewMaterials,
      canCreateMaterialCategories,
      canEditMaterialCategories,
      canDeleteMaterialCategories,
      canCreateMaterials,
      canEditMaterials,
      canDeleteMaterials
    };
  }
};
</script>